/**
 * The "What's new" releases the user can actually see — the one filtering the
 * pane and the unseen-release signals (`agentChangelogSeen.ts`: header badge,
 * launcher dot) must agree on. Each item id is a key of the Tolgee namespace
 * `AssistantChangelog`; an id without a translation yields "" (`orEmpty`) and
 * is hidden — never shown raw — and a release left without items is hidden
 * too. Sharing this keeps the badge from lighting up for a release the pane
 * would not list.
 */
import { getDataOrNull } from "@msys/common";
import type { TranslationKey } from "@msys/tolgee";
import { useTranslate } from "@tolgee/react";
import { useAgentChangelogQuery } from "./AgentChangelog.generated.js";

export type VisibleChangelogRelease = {
  id: string;
  version: string;
  releasedOn: string;
  items: { id: string; text: string }[];
};

export type ChangelogOptions = {
  /** `false` skips the query (no assistant access); defaults to `true`. */
  enabled?: boolean;
};

/**
 * The one set of options every `agentChangelog` query in this feature uses.
 * The query is mounted on every page (launcher news dot), so a failure must
 * stay silent — `noErrorNotification` stops the Apollo error link from
 * toasting it — and it must not be issued at all without assistant access.
 */
export function agentChangelogQueryOptions({
  enabled = true,
}: ChangelogOptions = {}) {
  return {
    skip: !enabled,
    context: { noErrorNotification: true },
  };
}

/**
 * The visible releases, newest first, or `null` while loading, while skipped
 * (`enabled: false`), on a query error, or on a
 * `MissingCapabilities`/`MissingPermissions` refusal (an empty array means
 * "loaded, nothing to show").
 */
export function useVisibleChangelogReleases(
  options: ChangelogOptions = {}
): VisibleChangelogRelease[] | null {
  const { t } = useTranslate(["AssistantChangelog"]);
  const { data, error } = useAgentChangelogQuery(
    agentChangelogQueryOptions(options)
  );

  if (error || !data) return null;
  const result = getDataOrNull(data.agentChangelog);
  if (!result) return null;

  return result.releases.flatMap(release => {
    const items = release.items.flatMap(id => {
      const text = t(id as TranslationKey<"AssistantChangelog">, {
        ns: "AssistantChangelog",
        orEmpty: true,
      });
      return text === "" ? [] : [{ id, text }];
    });
    return items.length === 0
      ? []
      : [
          {
            id: release.id,
            version: release.version,
            releasedOn: release.releasedOn,
            items,
          },
        ];
  });
}
